import { VoyageRoute, RoutingJobOptions, VoyageRoutePointFeature } from '../models/voyage-routing-model';
import { getDistance as haversineDist } from 'ol/sphere';
import { authFetchPOST, authFetchGET } from '../utils/auth-fetch';
import { WeatherModel } from '../models/weather-model';
import { convertWeatherLimitsToRoutingAPI } from '../utils/weather-limits';
import { Feature } from 'ol';
import { VoyagePlan } from '../models/vessel-planning-model';
import { CoordinatesUtils } from '../utils/coordinates-utils';

const env = (<any>window)["appenv"].services.routing;

const NMToMeters = 1852; // Nautical miles -> meters
const IgnoreRouteNetworkBelowNM = 300;

export const RoutingService = new class extends EventTarget {
    async postRoutingJob(route: VoyageRoute, options: RoutingJobOptions): Promise<string|null> {

        const draft = options.draft!;
        const routeType = options.routeType;
        const encValidation = options.encValidation;
        const etd = options.etd || Date.now();
        const eta = options.eta;
        const shipProfile = options.shipModel!.shipProfile;
        const speedRanges = options.shipModel!.speedRanges;
        const weatherLimits = convertWeatherLimitsToRoutingAPI(options.weatherLimits);
        const forecast_versions = [options.forecastVersion || WeatherModel!.latestForecastVersion];

        const waypoints = this._getFixedWaypoints(route);

        let payload: {[index:string]: any} = {};
        if (routeType === 'ShortestDistance') {
            payload = {
                "route_parameters": {
                    "route_type": routeType,
                    ship_profile: {
                        ship_measurements: Object.assign({}, shipProfile.ship_measurements),
                        safety_contour: shipProfile.safety_contour,
                        ship_dynamics: shipProfile.ship_dynamics,
                        ship_type: shipProfile.ship_type
                    },
                    "restrictions": {
                        "passages": {
                            "default_status": "Default"
                        },
                        "enc_hazards": [
                            {
                                "id": 0,
                                "severity": 4
                            },
                            {
                                "id": 1,
                                "severity": 4
                            },
                            {
                                "id": 2,
                                "severity": 4
                            },
                            {
                                "id": 3,
                                "severity": 4
                            },
                            {
                                "id": 4,
                                "severity": 4
                            },
                            {
                                "id": 5,
                                "severity": 4
                            },
                            {
                                "id": 6,
                                "severity": 4
                            },
                            {
                                "id": 7,
                                "severity": 4
                            },
                            {
                                "id": 100,
                                "severity": 4
                            },
                            {
                                "id": 101,
                                "severity": 4
                            },
                            {
                                "id": 102,
                                "severity": 4
                            },
                            {
                                "id": 103,
                                "severity": 4
                            },
                            {
                                "id": 104,
                                "severity": 4
                            },
                            {
                                "id": 105,
                                "severity": 4
                            },
                            {
                                "id": 106,
                                "severity": 4
                            },
                            {
                                "id": 107,
                                "severity": 4
                            },
                            {
                                "id": 108,
                                "severity": 4
                            },
                            {
                                "id": 109,
                                "severity": 4
                            },
                            {
                                "id": 110,
                                "severity": 4
                            },
                            {
                                "id": 111,
                                "severity": 4
                            },
                            {
                                "id": 112,
                                "severity": 4
                            },
                            {
                                "id": 113,
                                "severity": 4
                            },
                            {
                                "id": 114,
                                "severity": 4
                            },
                            {
                                "id": 115,
                                "severity": 4
                            },
                            {
                                "id": 116,
                                "severity": 4
                            },
                            {
                                "id": 117,
                                "severity": 4
                            },
                            {
                                "id": 118,
                                "severity": 4
                            },
                            {
                                "id": 119,
                                "severity": 4
                            },
                            {
                                "id": 120,
                                "severity": 4
                            },
                            {
                                "id": 121,
                                "severity": 4
                            },
                            {
                                "id": 122,
                                "severity": 4
                            },
                            {
                                "id": 123,
                                "severity": 4
                            },
                            {
                                "id": 124,
                                "severity": 4
                            },
                            {
                                "id": 125,
                                "severity": 4
                            },
                            {
                                "id": 126,
                                "severity": 4
                            },
                            {
                                "id": 127,
                                "severity": 4
                            },
                            {
                                "id": 128,
                                "severity": 4
                            },
                            {
                                "id": 129,
                                "severity": 4
                            },
                            {
                                "id": 130,
                                "severity": 4
                            },
                            {
                                "id": 200,
                                "severity": 4
                            },
                            {
                                "id": 201,
                                "severity": 4
                            },
                            {
                                "id": 202,
                                "severity": 4
                            },
                            {
                                "id": 203,
                                "severity": 4
                            },
                            {
                                "id": 204,
                                "severity": 4
                            },
                            {
                                "id": 205,
                                "severity": 4
                            },
                            {
                                "id": 206,
                                "severity": 4
                            },
                            {
                                "id": 207,
                                "severity": 4
                            },
                            {
                                "id": 208,
                                "severity": 4
                            },
                            {
                                "id": 209,
                                "severity": 4
                            },
                            {
                                "id": 210,
                                "severity": 4
                            },
                            {
                                "id": 211,
                                "severity": 4
                            }
                        ]
                    }
                },
                "fixed_waypoints": waypoints
            };
        } else {
            payload = {
                "route_parameters": {
                    "route_type": routeType,
                    "weather": {
                        forecast_versions,
                        "fallback_strategy": "Climatic"
                    },
                    speed_ranges: JSON.parse(JSON.stringify(speedRanges)), // Use a deep copy - since we may change some values
                    "etd": new Date(etd).toISOString(),
                    "eta": eta ? new Date(eta).toISOString() : undefined,
                    "time_between_waypoints": 6,
                    "costs": {
                        "fuel_cost": 100,
                        "light_fuel_cost": 120,
                        "other_fuel_cost": 50,
                        "vessel_cost": 50
                    },
                    "optimization_type": "Cost",
                    ship_profile: JSON.parse(JSON.stringify(shipProfile)), // Use a deep copy - since we may change some values
                    "restrictions": {
                        weather_limits: weatherLimits,
                        "passages": {
                            "default_status": "Default"
                        },
                        "enc_hazards": [
                            {
                                "id": 0,
                                "severity": 4
                            },
                            {
                                "id": 1,
                                "severity": 4
                            },
                            {
                                "id": 2,
                                "severity": 4
                            },
                            {
                                "id": 3,
                                "severity": 4
                            },
                            {
                                "id": 4,
                                "severity": 4
                            },
                            {
                                "id": 5,
                                "severity": 4
                            },
                            {
                                "id": 6,
                                "severity": 4
                            },
                            {
                                "id": 7,
                                "severity": 4
                            },
                            {
                                "id": 100,
                                "severity": 4
                            },
                            {
                                "id": 101,
                                "severity": 4
                            },
                            {
                                "id": 102,
                                "severity": 4
                            },
                            {
                                "id": 103,
                                "severity": 4
                            },
                            {
                                "id": 104,
                                "severity": 4
                            },
                            {
                                "id": 105,
                                "severity": 4
                            },
                            {
                                "id": 106,
                                "severity": 4
                            },
                            {
                                "id": 107,
                                "severity": 4
                            },
                            {
                                "id": 108,
                                "severity": 4
                            },
                            {
                                "id": 109,
                                "severity": 4
                            },
                            {
                                "id": 110,
                                "severity": 4
                            },
                            {
                                "id": 111,
                                "severity": 4
                            },
                            {
                                "id": 112,
                                "severity": 4
                            },
                            {
                                "id": 113,
                                "severity": 4
                            },
                            {
                                "id": 114,
                                "severity": 4
                            },
                            {
                                "id": 115,
                                "severity": 4
                            },
                            {
                                "id": 116,
                                "severity": 4
                            },
                            {
                                "id": 117,
                                "severity": 4
                            },
                            {
                                "id": 118,
                                "severity": 4
                            },
                            {
                                "id": 119,
                                "severity": 4
                            },
                            {
                                "id": 120,
                                "severity": 4
                            },
                            {
                                "id": 121,
                                "severity": 4
                            },
                            {
                                "id": 122,
                                "severity": 4
                            },
                            {
                                "id": 123,
                                "severity": 4
                            },
                            {
                                "id": 124,
                                "severity": 4
                            },
                            {
                                "id": 125,
                                "severity": 4
                            },
                            {
                                "id": 126,
                                "severity": 4
                            },
                            {
                                "id": 127,
                                "severity": 4
                            },
                            {
                                "id": 128,
                                "severity": 4
                            },
                            {
                                "id": 129,
                                "severity": 4
                            },
                            {
                                "id": 130,
                                "severity": 4
                            },
                            {
                                "id": 200,
                                "severity": 4
                            },
                            {
                                "id": 201,
                                "severity": 4
                            },
                            {
                                "id": 202,
                                "severity": 4
                            },
                            {
                                "id": 203,
                                "severity": 4
                            },
                            {
                                "id": 204,
                                "severity": 4
                            },
                            {
                                "id": 205,
                                "severity": 4
                            },
                            {
                                "id": 206,
                                "severity": 4
                            },
                            {
                                "id": 207,
                                "severity": 4
                            },
                            {
                                "id": 208,
                                "severity": 4
                            },
                            {
                                "id": 209,
                                "severity": 4
                            },
                            {
                                "id": 210,
                                "severity": 4
                            },
                            {
                                "id": 211,
                                "severity": 4
                            }
                        ]
                    }
                },
                "fixed_waypoints": waypoints
            };
        }

        // Update draft/airDraft
        const shipHeight = shipProfile.ship_measurements.draft + shipProfile.ship_measurements.air_draft;
        payload.route_parameters.ship_profile.ship_measurements.draft = draft;
        payload.route_parameters.ship_profile.ship_measurements.air_draft = shipHeight - draft;

        // Add dangerous_cargo
        payload.route_parameters.ship_profile.dangerous_cargo = options.dangerousCargo ? true : false;

        // Allow the ship dimensions, margins and speed to be overridden (for testing purposes)
        if (options.airDraft) {
            payload.route_parameters.ship_profile.ship_measurements.air_draft = options.airDraft;
        }
        if (options.lpp) {
            payload.route_parameters.ship_profile.ship_measurements.lpp = options.lpp;
        }
        if (options.beam) {
            payload.route_parameters.ship_profile.ship_measurements.beam = options.beam;
        }
        if (options.blockCoefficient)
        {
            payload.route_parameters.ship_profile.ship_measurements.cb = options.blockCoefficient;
        }
        if (options.aftMargin !== undefined) {
            payload.route_parameters.ship_profile.safety_contour.aft_margin = options.aftMargin;
        }
        if (options.airMargin !== undefined) {
            payload.route_parameters.ship_profile.safety_contour.air_margin = options.airMargin;
        }
        if (options.draftMargin !== undefined) {
            payload.route_parameters.ship_profile.safety_contour.draft_margin = options.draftMargin;
        }
        if (options.forwardMargin !== undefined) {
            payload.route_parameters.ship_profile.safety_contour.forward_margin = options.forwardMargin;
        }
        if (options.portsideMargin !== undefined) {
            payload.route_parameters.ship_profile.safety_contour.portside_margin = options.portsideMargin;
        }
        if (options.starboardMargin !== undefined) {
            payload.route_parameters.ship_profile.safety_contour.starboard_margin = options.starboardMargin;
        }
        if (options.minSpeed !== undefined && payload.route_parameters.speed_ranges) {
            payload.route_parameters.speed_ranges.forEach((r: { min: number | undefined; }) => r.min = options.minSpeed);
        }
        if (options.maxSpeed !== undefined && payload.route_parameters.speed_ranges) {
            payload.route_parameters.speed_ranges.forEach((r: { max: number | undefined; }) => r.max = options.maxSpeed);
        }

        const headers = !encValidation ? { "X-MG-Job-Override": "Optimization" } : undefined;
        const postResult = await authFetchPOST(env.scope, `${env.baseurl}/api/v5/calculations`, payload, headers);
        if (!postResult || !postResult.job_id) {
            console.warn('Error returned from MeteoGroup API');
            return null;
        }

        // For SMEs:
        console.log(`Routing job submitted with ID: '${postResult.job_id}':`, payload);

        return postResult.job_id;
    }

    async getResultForJob(jobId: string, inputRoute?: VoyageRoute)
    {
        let status = "";
        let result = null;
        while (status !== "Failed" && status !== "Done")
        {
            result = await authFetchGET(env.scope, `${env.baseurl}/api/v5/calculations/${jobId}`);
            status = result.status ? result.status : "Failed";
            if (status !== "Failed" && status !== "Done")
            {
                await new Promise(resolve => setTimeout(resolve, 2000)); // Wait two seconds between polls
            }
        }

        if (status === "Done")
        {
            this._processResult(result, inputRoute);
        }

        return result;
    }

    private _getFixedWaypoints(route: VoyageRoute) {
        const waypoints = [];

        if (route.route_geometry && route.route_geometry.length) {
            const fixedPoints = route.route_geometry.filter(feature =>
                feature.type === "Feature" &&
                feature.geometry.type === "Point" &&
                feature.properties.type === "Hard" );

            let prevPoint, ignoreRN;
            for (const point of fixedPoints) {
                ignoreRN = false;

                if (prevPoint && fixedPoints.length > 2) {
                    const dist = haversineDist(prevPoint.geometry.coordinates,
                        point.geometry.coordinates) / NMToMeters;
                    ignoreRN = dist < IgnoreRouteNetworkBelowNM;
                }

                waypoints.push({
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: point.geometry.coordinates
                    },
                    properties: {
                        ignore_route_network: ignoreRN ? true : undefined
                    }
                });
                prevPoint = point;
            }
        }

        return waypoints;
    }

    private _processResult(result: any, inputRoute?: VoyageRoute): void
    {
        const route: VoyageRoute = result.status_info;

        // Remove weather points (they are useless to us anyway)
        route.route_geometry = route.route_geometry.filter( (feature) => feature.geometry.type !== "Point" || feature.properties.type !== "Weather");

        // Fixup LineStrings - with v4 of the DTN API the course property is missing from them
        for (const feature of route.route_geometry)
        {
            if (feature.geometry.type === "LineString")
            {
                // Course is on the end waypoint - find and copy it
                const endCoordinates = feature.geometry.coordinates[1];
                const endPoint = route.route_geometry.find( e => e.geometry.type === "Point" &&
                                                                 e.geometry.coordinates[0] === endCoordinates[0] &&
                                                                 e.geometry.coordinates[1] === endCoordinates[1]);
                if (endPoint && endPoint.properties.course !== undefined)
                {
                    feature.properties.course = endPoint.properties.course;
                }
            }
        }

        // Add etd to route (only eta is part of status_info) and add missing timestamp on first waypoint
        route.route_info!.etd = result.request.route_parameters.etd;
        route.route_geometry[0].properties.timestamp = route.route_info!.etd;

        // Set type to Navigational for all waypoints (actual Hard points will be set below)
        for (const feature of route.route_geometry)
        {
            if (feature.geometry.type === "Point")
            {
                (<VoyageRoutePointFeature>feature).properties.type = "Navigational";
            }
        }

        // Re-add any extra properties to the waypoints, overwriting type
        if (inputRoute)
        {
            for (const inputFeature of inputRoute.route_geometry)
            {
                if (inputFeature.geometry.type === "Point")
                {
                    const coordinates = inputFeature.geometry.coordinates;
                    const outputPoint = route.route_geometry.find( e => e.geometry.type === "Point" &&
                                                                        e.geometry.coordinates[0] === coordinates[0] &&
                                                                        e.geometry.coordinates[1] === coordinates[1]);
                    if (outputPoint)
                    {
                        for (const propName in inputFeature.properties)
                        {
                            if (outputPoint.properties[propName] === undefined || propName === "type")
                            {
                                outputPoint.properties[propName] = inputFeature.properties[propName];
                            }
                        }
                    }
                }
            }
        }
    }
};

